<template>
  <div class="container">
    <div class="product-hero px-2 md:px-0 row lg:block clearfix justify-center">
      <!-- Name and description -->
      <div class="col-12 xl:col-6 float-right order-0 relative mb-4 xl:mb-0">
        <header class="pb-1 text-center xl:text-left">
          <div class="product-breadcrumb">
            <BreadCrumb
              :breadcrumb-list="breadcrumbList"
              additional-class-name="justify-center xl:justify-start lg:pl-7 xl:pl-0"
            />
          </div>
          <h1 class="product-hero__title heading-4 mb-2 md:mb-1">
            {{ product.name }}
          </h1>
          <div class="flex items-center justify-center xl:justify-start">
            <ProductStarsRating
              v-if="product.starRating && parseInt(product.starRating) > 0"
              :rating="product.starRating"
            />
            <div
              v-if="product.commentsSum > 0"
              class="to-comments underline pointer"
              @click="scrollToTabControl(3)"
            >
              {{ product.commentsSum }} {{ ratingLabel }}
            </div>
          </div>
          <p
            v-if="product.positives && product.positives.length > 0"
            class="mb-0 mt-2 text-sm"
            v-html="product.positives"
          />
        </header>
      </div>
      <!-- Image (gallery) -->
      <div class="col-12 md:col-6 xl:pt-5 float-left order-1">
        <div class="relative px-1 md:pl-0 md:pr-4 lg:ml-n6">
          <ProductItemToast
            :toast-operation="toastObject"
          />
          <div class="product-hero__bubble product-hero__bubble--top pattern-2 hidden lg:block" />
          <div class="product-hero__bubble product-hero__bubble--bottom pattern-1 hidden lg:block" />
          <DiscountBubble
            v-if="product.hasDiscount || (product.plusClubPrice !== null && getCurrentCustomer?.inPlusClub)"
            :price-with-discount="parseFloat((product.plusClubPrice !== null && getCurrentCustomer?.inPlusClub) ? product.plusClubPrice : product.price)"
            :price-without-discount="parseFloat(product.priceWithoutDiscount)"
            :currency="product.currency"
            class="lg:mt-6 lg:ml-6"
          />
          <!-- Carousel -->
          <div class="product-carousel__wrapper">
            <ProductCarousel :images="sortedProductImages" />
          </div>
          <!-- Add to favorite, like, new -->
          <ul
            class="product-hero__tags flex items-center flex-col absolute top-0 right-0 list-unstyled pt-6 lg:pt-0 xl:pt-2 lg:mt-7 mr-n1 lg:mr-5"
          >
            <li
              v-if="isRecommendedProduct"
              class="lg:mb-1 flex flex-col text-success"
            >
              <i class="mi-recommended icon-36" />
            </li>
            <li
              v-if="isNewProduct"
              class="flex flex-col text-danger"
            >
              <i class="mi-new icon-36" />
            </li>
          </ul>
        </div>
      </div>
      <!-- Other information -->
      <div
        v-if="product.categories[1].systemName !== 'ready_order_gifts'"
        class="col-12 md:col-8 lg:col-6 mx-auto float-right order-2 md:order-3 mt-3"
      >
        <!-- Line for mobile and tablet -->
        <hr class="my-5 hidden md:block lg:hidden border-primary opacity-20">
        <!-- Product variants -->
        <div
          v-if="productVariants && productVariants.length > 0"
          class="w-100 mb-5 order-1 px-1 product-variants"
        >
          <div class="block lg:inline-block">
            <ProductVariantsDropdown :products="productVariants" />
          </div>
        </div>
        <!-- Price, weight, tags -->
        <div
          class="row items-start mt-n3 mb-4 pt-3 md:pt-0"
          :class="{ 'mt-4 md:mt-5': !(product.variants && product.variants.productVariants.length > 0) }"
        >
          <!-- Price, weight -->
          <div
            v-if="!isHiddenProduct"
            class="col-auto order-0 mt-3 xl:mr-4"
          >
            <p class="flex mb-0 text-nowrap">
              <strong class="h2 block mb-0 mr-1 font-family-base">
                <span
                  :class="{
                    'line-through text-danger': product.plusClubPrice !== null && getCurrentCustomer?.inPlusClub,
                  }"
                >
                  <ProductPrice
                    :price-first="product.price"
                    :currency="product.currency"
                  />
                </span>
                <span v-if="product.weight > 0 || product.volue > 0"> /</span>
              </strong>
              <span
                v-if="product.weight > 0 || product.volume > 0"
                class="h2 block m-0 font-family-base font-normal text-muted"
              >{{ product.unit === 'g' ? product.weight : product.volume }} {{ product.unit }}</span>
              <span v-if="product.hasDiscount && (product.productPack && product.productPack.products.length > 0)">
                <button
                  v-tooltip="{
                    content: t('pack_discount_tooltip'),
                    theme: 'checkoutTooltip',
                  }"
                  class="btn btn-outline-warning p-1 ml-2 mt-0 sm:mt-1 rounded-circle"
                  type="button"
                  aria-label="info"
                  @click.prevent
                >
                  <i class="mi-question icon-12 mi-no-mx mi-lh-1" />
                </button>
              </span>
            </p>
            <p class="mb-0">
              <strong
                v-if="product.hasDiscount && (product.plusClubPrice === null || !getCurrentCustomer?.inPlusClub)"
                class="line-through text-danger h3 mb-0 mr-3 lg:mr-4 font-family-base"
              >
                <ProductPrice
                  :price-first="product.priceWithoutDiscount"
                  :currency="product.currency"
                />
              </strong>
              <span
                v-if="product.hasDiscount && product.plusClubPrice !== null && !product.price"
                class="h3 font-family-base text-nowrap text-muted"
              >
                / {{ product.weight }} g
              </span>
            </p>
            <p
              v-if="product.pricePerPortion"
              class="small mb-0"
            >
              ({{ numberToCurrency(parseFloat(product.pricePerPortion).toFixed(2), product.currency) }} / 100 {{ product.unit }})
            </p>
          </div>
          <!-- Adds to cart, adds, removes, share -->
          <div class="form-row product-buttons mt-3 order-2 xl:order-1 mr-l xl:ml-0">
            <!-- Adds to cart -->
            <div
              v-if="product.isReadyProduct || product.isGift"
              class="col lg:col-auto"
            >
              <AddToCartButton
                v-if="product.state === 'available'"
                :show-decrement-button="true"
                :product="product"
                :wide="true"
                theme="transparent"
                :odometer-fill="true"
                @cart-operation="createToast"
              />
              <ButtonState
                v-else
                :state="product.state"
                :product="product"
                added-class="w-100"
              />
            </div>
            <div
              v-else-if="product.isIngredient"
              class="col lg:col-auto pt-2"
            >
              <PushToMixButton
                v-if="product.state === 'available'"
                :product="product"
                added-class="w-100"
              />
              <ButtonState
                v-else
                :state="product.state"
                :product="product"
                added-class="w-100"
              />
            </div>
            <!-- Adds to favorite -->
            <div class="col-auto flex m-0 relative zindex-2">
              <Favorite
                v-if="!product.isIngredient"
                :id="product.id"
                box-class="m-0"
                btn-class="btn  btn-info stretched-link rounded-pill border-0 p-2 relative"
                icon-size="28"
                icon-color-class="text-white"
              />
              <ButtonShare
                v-if="!isHiddenProduct"
                :link="productShowLink"
                class="col-auto"
                :icon-only="true"
              />
            </div>
          </div>
          <div
            v-if="product.plusClubPrice"
            class="flex w-100 items-center mt-2 order-1 xl:order-2 col-auto flex-wrap"
          >
            <p class="mb-1">
              <strong class="h2 block mb-0 mr-2 font-family-base">
                <ProductPrice
                  :price-first="product.plusClubPrice"
                  :currency="product.currency"
                />
              </strong>
            </p>
            <MixitPlusTagSvg class="mr-2" />
            <div class="col-auto px-0 mixit-plus-flex">
              <router-link
                :to="{ name: 'productPath', params: { permalink: t('voucher_permalink') } }"
                class="flex justify-start items-center flex-nowrap mb-1 no-decoration"
              >
                <span class="flex border border-warning border-2 text-warning p-1 rounded-circle">
                  <i class="mi-question icon-12" />
                </span>
                <span class="text-sm text-nowrap ml-1">{{ t('for_mixit_plus') }}</span>
                <MixitPlusLogoSvg
                  width="94"
                  class="ml-1 relative mt-1"
                />
              </router-link>
            </div>
          </div>
        </div>
        <!-- Tags -->
        <ProductPropertiesList
          :properties="filteredProperties"
          class="col-auto lg:col pt-0 pl-0"
        />
      </div>
      <!-- Quantity discount progress -->
      <QuantityDiscountProgress
        v-if="showDiscountProgress()"
        :product="product"
      />
      <div>
        <!-- Description + ean -->
        <div class="col-12 md:col-6 mb-0 lg:mb-5 float-right order-3 md:order-2">
          <div class="text-sm mt-6 md:mt-0 lg:mt-7">
            <p
              v-if="product.state === 'weather_too_hot'"
              class="font-bold text-info"
            >
              {{ t('selected_chocolate_products') }}
            </p>
            <div
              v-if="!props.product.quantityDiscounts"
              class="product-description-text relative"
            >
              <p v-html="product.description" />
              <span
                class="to-description pointer absolute right-0"
                @click="scrollToTabControl(1)"
              ><span>...</span><span class="underline font-bold">{{ t('read_more') }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { striptags } from 'striptags'
import { computed, defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import useHead from '@/mixins/headManager'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { getAllImageSizeByFormat } from '@/mixins/utils'
import { useAppStore } from '@/stores/AppStore'

const QuantityDiscountProgress = defineAsyncComponent(
  () => import('@/components/products/QuantityDiscountProgress.vue'),
)

const ProductVariantsDropdown = defineAsyncComponent(() => import('@/components/products/ProductVariantsDropdown.vue'))
const ProductStarsRating = defineAsyncComponent(() => import('@/components/products/ProductStarsRating.vue'))
const AddToCartButton = defineAsyncComponent(() => import('@/components/buttons/ButtonAddToCartButton.vue'))
const PushToMixButton = defineAsyncComponent(() => import('@/components/buttons/ButtonPushToMixButton.vue'))
const ButtonShare = defineAsyncComponent(() => import('@/components/buttons/ButtonShare.vue'))
const ButtonState = defineAsyncComponent(() => import('@/components/buttons/ButtonState.vue'))
const Favorite = defineAsyncComponent(() => import('@/components/buttons/ButtonFavorite.vue'))
const BreadCrumb = defineAsyncComponent(() => import('@/components/general/BaseBreadcrumb.vue'))
const DiscountBubble = defineAsyncComponent(() => import('@/components/general/BaseDiscountBubble.vue'))
const ProductCarousel = defineAsyncComponent(() => import('@/components/products/ProductCarousel.vue'))
const ProductPropertiesList = defineAsyncComponent(() => import('@/components/products/ProductPropertiesList.vue'))
const ProductPrice = defineAsyncComponent(() => import('@/components/products/ProductPrice.vue'))

const MixitPlusLogoSvg = defineAsyncComponent(() => import('@/components/svg/MixitPlusLogoSvg.vue'))
const MixitPlusTagSvg = defineAsyncComponent(() => import('@/components/svg/MixitPlusTagSvg.vue'))
const ProductItemToast = defineAsyncComponent(() => import('@/components/products/ProductItemToast.vue'))

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  productVariants: {
    type: Object,
    required: true,
  },
  isHiddenProduct: {
    type: Boolean,
    required: true,
  },
  commentsCount: {
    type: Number,
    required: true,
  },
})

const toastObject = ref();
const createToast = (operation) => {
  toastObject.value = operation;
}

const showDiscountProgress = () => {
  if (props.product.quantityDiscounts) {
    if (props.product.plusClubPrice) {
      if (getCurrentCustomer.value?.inPlusClub) {
        return false
      }
      return true
    }
    return true
  }
  return false
}

const appStore = useAppStore()
const { getCurrentCustomer } = storeToRefs(appStore)

const sortedProductImages = computed(() => {
  return [...props.product.images].sort((a, b) => Number(b.main_image) - Number(a.main_image))
})

const emit = defineEmits(['changeActiveTab'])

const scrollToTabControl = (id) => {
  window.scrollTo({
    behavior: 'smooth',
    top: document.querySelector('#tabControl').offsetTop + document.querySelector('#main').offsetTop - 20,
  })
  emit('changeActiveTab', id)
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const url = (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.path
const description = props.product.description ? striptags(props.product.description) : ''
const urlList = computed(() => getAllImageSizeByFormat(sortedProductImages.value[0].data, 'jpeg'))
const src = computed(() => urlList.value.llg)
const categoriesPath =
  props.product.isReadyProduct || props.product.isGift
    ? router.resolve({
        name: 'productsPath',
        params: {
          permalink: props.product.categories[1].permalink,
        },
      })
    : router.resolve({
        name: 'mixesPath',
        params: {
          permalink: props.product.mixableToCategories[0].permalink,
        },
      })

const productAvailability = computed(() => {
  if (props.product.state === 'available') {
    return 'https://schema.org/InStock'
  } else if (props.product.state === 'hidden' || props.product.state === 'removed') {
    return 'https://schema.org/Discontinued'
  } else {
    return 'https://schema.org/OutOfStock'
  }
})

const productJsonld = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  'brand': {
    '@type': 'Brand',
    'name': 'Mixit',
  },
  'image': src.value,
  'name': props.product.name,
  'category':
      props.product.isReadyProduct || props.product.isGift
          ? props.product.categories[1].title
          : props.product.mixableToCategories[0].title,

  'description': props.product.shortDescription,
  'offers': {
    '@type': 'Offer',
    'price': Number(props.product.price).toFixed(2),
    'priceCurrency': props.product.currencyCode,
    url,
    'itemCondition': 'https://schema.org/NewCondition',
    'availability': productAvailability.value,
  },
}

const breadcrumbJsonld = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': t('main_page'),
      'item': baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name':
          props.product.isReadyProduct || props.product.isGift
              ? props.product.categories[1].title
              : props.product.mixableToCategories[0].title,

      'item': (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + categoriesPath.href,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': props.product.name,
    },
  ],
}

if (props.product.starRatingCommentsSum > 0) {
  productJsonld.aggregateRating = {
    "@type": "AggregateRating",
    "ratingValue": props.product.starRating,
    "ratingCount": props.product.starRatingCommentsSum,
  };
}

const jsonld = [productJsonld, breadcrumbJsonld];

useHead({
  title: props.product.name,
  meta: [
    {
      property: `og:title`,
      content: computed(() => props.product.name),
    },
    {
      property: `og:description`,
      content: description,
    },
    {
      name: `description`,
      content: description,
    },
    {
      property: `og:url`,
      content: url,
    },
    {
      property: `og:image`,
      content: src,
    },
    {
      property: `og:type`,
      content: 'product',
    },
    {
      name: 'keywords',
      content: props.product.tags || props.product.name,
    },
    // {
    //   name: 'robots',
    //   content: props.product.state === 'removed' ? 'noindex,nofollow' : 'index,follow',
    // },
  ],
  link: [
    {
      rel: 'canonical',
      href: url,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(jsonld),
    },
  ],
})

const breadcrumbList = computed(() => {
  const list = []
  if (props.product.isReadyProduct || props.product.isGift) {
    list.push({
      name: props.product.categories[1].title,
      link: { name: 'productsPath', params: { permalink: props.product.categories[1].permalink } },
    })
  } else if (props.product.isIngredient) {
    list.push({
      name: props.product.mixableToCategories[0].title,
      link: { name: 'mixesPath', params: { permalink: props.product.mixableToCategories[0].permalink } },
    })
  }
  list.push({
    name: props.product.name,
    link: { name: 'productPath', params: { permalink: props.product.permalink } },
  })
  return list
})

const productShowLink = computed(() => {
  return (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.fullPath
})

const marketingProperties = computed(() => {
  if (!props.product) return []
  return props.product.properties.filter((p) => p.propertyType === 'marketing')
})

const filteredProperties = computed(() => {
  return marketingProperties.value.filter((p) => p.systemName !== 'new' && p.systemName !== 'recommended')
})

const isNewProduct = computed(() => {
  return marketingProperties.value.some((p) => p.systemName === 'new')
})

const isRecommendedProduct = computed(() => {
  return marketingProperties.value.some((p) => p.systemName === 'recommended')
})

const ratingLabel = computed(() => {
  if (props.product.commentsSum === 1) {
    return t('ratings.one')
  } else if (props.product.commentsSum >= 2 && props.product.commentsSum <= 4) {
    return t('ratings.few')
  }
  return t('ratings.other')
})
</script>

<style lang="scss" scoped>
header {
  min-height: 80px;

  .product-breadcrumb {
    min-height: 40px;
  }
}

.product-carousel__wrapper {
  min-height: 86vw;
  height: 100%;

  @media screen and (min-width: 575px) {
    min-height: 340px;
  }

  @media screen and (min-width: 992px) {
    min-height: 500px;
  }

  @media screen and (min-width: 1200px) {
    min-height: 562px;
  }
}

.product-buttons {
  @media screen and (max-width: 1199px) {
    width: 100%;
  }
}

.to-comments {
  min-height: 24px;
}

.to-description {
  bottom: -4px;

  &:before {
    content: '';
    position: absolute;
    width: 180%;
    height: 100%;
    bottom: 0;
    right: 0;
    background-image: linear-gradient(to left, rgba(247, 231, 209, 1) 60%, rgba(247, 231, 209, 0));
  }

  span {
    z-index: 2;
    position: relative;
  }
}

.product-variants {
  height: 48px;
}

.product-description-text {
  height: 100px;
  overflow: hidden;

  @media screen and (max-width: 991px) {
    display: none;
  }
}
.mixit-plus-svg {
  width: 86px;
}
.mixit-plus-flex {
  @media screen and (max-width: 500px) {
    flex-basis: 100%;
  }
}
.line-through {
  text-decoration: line-through;
}
</style>

<i18n>
  {
    "cs": {
    "for_mixit_plus": "pro členy",
    "ingredient": "Namixuj si",
    "main_page": "Hlavní strana",
    "pack_discount_tooltip": "Sleva je vypočítána jako rozdíl oproti nákupu obsažených produktů samostatně",
    "ratings": {
        "few": "hodnocení",
        "one": "hodnocení",
        "other": "hodnocení"
    },
    "read_more": "Číst více",
    "selected_chocolate_products": "Jsem u ledu! Z důvodu vysokých teplot mě poslali do chládku.",
    "voucher_permalink": "mixit-plus-na-12-mesicu"
},
    "sk": {
    "for_mixit_plus": "pre členov",
    "ingredient": "Namixuj si",
    "main_page": "Hlavná stránka",
    "pack_discount_tooltip": "Zľava sa počíta ako rozdiel oproti samostatnému nákupu zahrnutých produktov",
    "ratings": {
        "few": "hodnotenia",
        "one": "hodnotenie",
        "other": "hodnotení"
    },
    "read_more": "Prečítať si viac",
    "selected_chocolate_products": "Som v chládku! :( Čokoláda má kvôli vysokým teplotám dovolenku.",
    "voucher_permalink": "mixit-plus-na-12-mesiacov"
},
    "pl": {
    "for_mixit_plus": "dla członków",
    "ingredient": "Namixuj",
    "main_page": "Strona główna",
    "pack_discount_tooltip": "Zniżka jest obliczana jako różnica w porównaniu do zakupu poszczególnych produktów osobno.\n\n\n\n\n\n\n\n",
    "ratings": {
        "few": "oceny",
        "one": "ocena",
        "other": "ocen"
    },
    "read_more": "Czytaj więcej",
    "selected_chocolate_products": "Czekam w chłodzie! :( Ze względu na upały, czekolada została wysłana na przymusowy urlop.",
    "voucher_permalink": "mixit-plus-na-12-miesiecy"
},
    "hu": {
    "for_mixit_plus": "tagok számára",
    "ingredient": "Mixelji",
    "main_page": "Főoldal",
    "pack_discount_tooltip": "A kedvezményt a mellékelt termékek külön-külön történő megvásárlásának különbözeteként számítják ki.",
    "ratings": {
        "few": "értékelés",
        "one": "értékelés",
        "other": "értékelés"
    },
    "read_more": "Továbbolvasom",
    "selected_chocolate_products": "Hidegre helyezve! :( A magas hőmérsékletek miatt a csokoládés termékek szabadságon vannak.",
    "voucher_permalink": "mixit-plusz-12-honapra"
},
    "de": {
    "for_mixit_plus": "für Mitglieder",
    "ingredient": "Mix dir",
    "main_page": "Hauptseite",
    "pack_discount_tooltip": "Der Rabatt berechnet sich aus der Differenz zum separaten Kauf der enthaltenen Produkte.",
    "ratings": {
        "few": "Bewertung",
        "one": "Bewertung",
        "other": "Bewertung"
    },
    "read_more": "Mehr lesen",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "at": {
    "for_mixit_plus": "für Mitglieder",
    "ingredient": "Mix dir",
    "main_page": "Hauptseite",
    "pack_discount_tooltip": "Der Rabatt berechnet sich aus der Differenz zum separaten Kauf der enthaltenen Produkte.",
    "ratings": {
        "few": "Bewertung",
        "one": "Bewertung",
        "other": "Bewertung"
    },
    "read_more": "Mehr lesen",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "ro": {
    "for_mixit_plus": "pentru membri",
    "ingredient": "Amesteca",
    "main_page": "Pagina principală",
    "pack_discount_tooltip": "Reducerea este calculată ca diferență față de achiziția individuală a produselor incluse",
    "ratings": {
        "few": "evaluări",
        "one": "evaluare",
        "other": "evaluări"
    },
    "read_more": "mai mult",
    "selected_chocolate_products": "La rece! :( Din cauza temperaturilor înalte produsele cu ciocolată sunt în concediu.",
    "voucher_permalink": "mixit-plus-de-12-luni"
},
    "fr": {
    "for_mixit_plus": "pour les membres",
    "ingredient": "Crée ton mix",
    "main_page": "Page principale",
    "pack_discount_tooltip": "La remise est calculée comme la différence par rapport à l’achat des produits inclus séparément",
    "ratings": {
        "few": "évaluation",
        "one": "évaluation",
        "other": "évaluation"
    },
    "read_more": "En savoir plus",
    "selected_chocolate_products": "Je suis au frais ! À cause des températures élevées, ils m'ont plongée dans le froid.",
    "voucher_permalink": "mixit-plus-pour-12 mois"
}
  }
</i18n>
